import { t } from "@lingui/core/macro";
/* eslint-disable class-methods-use-this */
import { EMPTY_ARRAY, EMPTY_STRING, noop } from "@regrello/core-utils";
import { DataTestIds } from "@regrello/data-test-ids-api";
import type {
  FieldFields,
  FieldInstanceFields,
  PropertyTypeFields,
  SpectrumFieldConstraintFields,
  SpectrumFieldValidationTypeFields,
  SpectrumFieldVersionFields,
  SpectrumValueConstraintFields,
  UpdateFieldInstanceValueInputs,
} from "@regrello/graphql-api";
import { RegrelloIcon, type RegrelloIconName, type RegrelloIconProps, RegrelloPhoneInput } from "@regrello/ui-core";
import type { ColDef, ValueFormatterParams, ValueParserParams } from "ag-grid-community";
import { isValidPhoneNumber } from "libphonenumber-js/max";
import debounce from "lodash/debounce";
import type { ReactNode } from "react";
import type { FieldArrayWithId, FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import type { ConfigureSpectrumFieldFormFormFields } from "../../../../../../ui2/fields/configure/_internal/ConfigureSpectrumFieldForm";
import type { RowDatum } from "../../../../../../ui2/forms/instance/utils/tableV2Utils";
import { showToast } from "../../../../components/toast/useToastMessageQueue";
import type {
  CustomFieldPlugin,
  CustomFieldPluginV2RenderFormFieldProps,
  ValidateBulkUploadCellValueOptions,
} from "../../../customFields/plugins/types/CustomFieldPlugin";
import {
  type ValidationResult,
  validateRequired,
} from "../../../customFields/plugins/utils/validateBulkUploadCellValueUtils";
import { RegrelloControlledFormFieldPhone } from "../../../formFields/controlled/RegrelloControlledFormFieldPhone";
import type { ProspectiveSpectrumFieldVersion } from "../../types/ProspectiveSpectrumFieldVersion";
import { SpectrumFieldPluginDecorator } from "../../types/SpectrumFieldPluginDecorator";
import { SpectrumFieldValidationType } from "../../types/SpectrumFieldValidationType";
import { validateByConstraints } from "../../utils/spectrumFieldConstraintUtils";
import { RegrelloEditableCellPhone } from "./_internal/RegrelloEditableCellPhone";
import type { PhoneFieldPluginFrontendValue } from "./_internal/types";

type VALUE = PhoneFieldPluginFrontendValue; // Saves some characters without using a troublesome generic type.

const debouncedShowToast = debounce(showToast, 100);

export class SpectrumPhoneFieldPluginDecorator extends SpectrumFieldPluginDecorator<VALUE> {
  constructor(plugin: CustomFieldPlugin<string>) {
    super(plugin as CustomFieldPlugin<VALUE>); // Casting because VALUE is a super-type of string, but it's worked fine for months and I'm simply refactoring a bit.
    this.uri = "com.regrello.spectrumField.phone";
  }

  public canProcessValidationType(spectrumValidationType: ProspectiveSpectrumFieldVersion["validationType"]) {
    return spectrumValidationType.validationType === SpectrumFieldValidationType.PHONE;
  }

  public canProcessSpectrumField(field: ProspectiveSpectrumFieldVersion) {
    return (
      this.canProcessPropertyDataType(field.propertyType.dataType) &&
      this.canProcessValidationType(field.validationType)
    );
  }

  public findPropertyTypeFromLoadedPropertyTypes(propertyTypes: PropertyTypeFields[]) {
    return propertyTypes.find((propertyType) => propertyType.dataType === "STRING");
  }

  public findValidationTypeFromLoadedValidationTypes(validationTypes: SpectrumFieldValidationTypeFields[]) {
    return validationTypes.find(
      (validationType) => validationType.validationType === SpectrumFieldValidationType.PHONE,
    );
  }

  public findValueConstraintsFromLoadedValueConstraints(_valueConstraints: SpectrumValueConstraintFields[]) {
    return EMPTY_ARRAY;
  }

  public override getFieldDisplayName = () => {
    return t`Phone`;
  };

  public override getIconName = (): RegrelloIconName => {
    return "phone";
  };

  public override renderIcon = (props?: Omit<RegrelloIconProps, "iconName">) => {
    return <RegrelloIcon {...props} iconName="phone" />;
  };

  public getConstraintConditionOperators() {
    return {
      IS_ZERO: {
        label: t`Is empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      IS_NOT_ZERO: {
        label: t`Is not empty`,
        inputCount: 0,
        isMultiselect: false,
      },
      EQ: {
        label: t`Is equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
      NOT: {
        label: t`Is not equal to`,
        inputCount: 1,
        isMultiselect: false,
      },
      STRING_STARTS_WITH: {
        label: t`Starts with`,
        inputCount: 1,
        isMultiselect: false,
      },
      STRING_ENDS_WITH: {
        label: t`Ends with`,
        inputCount: 1,
        isMultiselect: false,
      },
    };
  }

  public getSpectrumFormAutosaveMode() {
    return "onBlur" as const;
  }

  public getConstraintsToDisplay(field: SpectrumFieldVersionFields): SpectrumFieldConstraintFields[] {
    return field.fieldConstraints;
  }

  public renderPreviewFormField() {
    return <RegrelloPhoneInput disabled={true} onChange={noop} size="medium" value="" />;
  }

  public renderValueConstraints(_props: {
    constraints: Array<FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">>;
    disabled: boolean;
    form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>;
  }): ReactNode {
    return null;
  }

  public override renderSpectrumFormField = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    _field: FieldFields,
    props: CustomFieldPluginV2RenderFormFieldProps<TFieldValues, TName>,
  ): React.ReactNode => {
    const isDeleted = props.fieldInstance?.field.deletedAt != null;
    return (
      <RegrelloControlledFormFieldPhone
        {...props}
        key={props.controllerProps.name}
        dataTestId={DataTestIds.CUSTOM_FIELD_VALUE_INPUT}
        infoTooltipText={props.description ?? undefined}
        isDeleted={isDeleted}
      />
    );
  };

  public validateBulkUploadCellValue(
    _field: FieldFields,
    value: string,
    options: ValidateBulkUploadCellValueOptions,
  ): ValidationResult {
    const normalizedValue = value.trim();

    const result = validateRequired(normalizedValue, options.isRequired);
    if (result != null) {
      return result;
    }

    const valueWithLeadingPlus = normalizedValue.startsWith("+") ? normalizedValue : `+${normalizedValue}`;

    const spectrumValidationResult = this.validateValueInternal(options.fieldVersion, valueWithLeadingPlus);
    if (!spectrumValidationResult.isValid) {
      return {
        value: normalizedValue,
        info: spectrumValidationResult.errors.map((errorString) => ({
          message: errorString,
          level: "error" as const,
        })),
      };
    }

    return { value: normalizedValue };
  }

  public getColumnDef<TData extends RowDatum>(
    colDefParams: Partial<ColDef<TData, VALUE>>,
    options: {
      fieldInstance: FieldInstanceFields;
      placeholderRenderer?: (params: ValueFormatterParams<TData, VALUE>) => string;
    },
  ): ColDef<TData, VALUE> {
    return {
      ...colDefParams,
      cellClassRules: {
        ...colDefParams.cellClassRules,
        "regrello-center-aligned-cell": (_) => true,
        "rg-grid-not-editable": (params) => {
          // (max): gray cell out if it is not editable.
          if (params.colDef.editable == null) {
            return true;
          }
          if (typeof params.colDef.editable === "boolean") {
            return !params.colDef.editable;
          }
          return !params.colDef.editable(params);
        },
      },
      cellEditor: RegrelloEditableCellPhone,
      cellEditorParams: {
        clearValueIfInvalid: this.clearValueIfInvalid,
        fieldInstance: options.fieldInstance,
      },
      cellEditorPopup: true,
      cellEditorPopupPosition: "over", // eslint-disable-line lingui/no-unlocalized-strings
      valueParser: (params: ValueParserParams<TData, VALUE>) => {
        const parsedValue = params.newValue?.trim() ?? EMPTY_STRING;
        const validOrEmptyValue = this.clearValueIfInvalid(parsedValue);
        if (validOrEmptyValue !== parsedValue) {
          debouncedShowToast({
            content: t`Invalid phone numbers were removed`,
            intent: "warning",
          });
        }
        return validOrEmptyValue;
      },
      cellRenderer: (params: ValueFormatterParams<TData, VALUE>): ReactNode => {
        if (params.value == null || params.value === "") {
          return options.placeholderRenderer ? options.placeholderRenderer(params) : "";
        }
        return String(params.value);
      },
    };
  }

  public getFieldUpdateInputsFromFrontendValue(
    fieldInstance: FieldInstanceFields,
    frontendValue: PhoneFieldPluginFrontendValue,
  ): UpdateFieldInstanceValueInputs {
    return {
      id: fieldInstance.values[0].id,
      inputType: fieldInstance.values[0].inputType,
      stringValue: frontendValue,
    };
  }

  public isValidTableColumn(_: SpectrumFieldVersionFields): boolean {
    return true;
  }

  public validateValue(
    fieldVersion: SpectrumFieldVersionFields,
    value: unknown,
    isRequired: boolean,
  ): { isValid: boolean; errors: string[] } {
    if (value == null || value === EMPTY_STRING) {
      if (isRequired) {
        return { isValid: false, errors: [t`This field is required`] };
      }
      return { isValid: true, errors: EMPTY_ARRAY };
    }

    if (typeof value !== "string") {
      console.error("Invalid value type for phone field plugin. This is a developer error.", value);
      return { isValid: false, errors: [t`Must be a string or null`] };
    }

    return this.validateValueInternal(fieldVersion, value);
  }

  private validateValueInternal(
    fieldVersion: SpectrumFieldVersionFields,
    value: VALUE,
  ): { isValid: boolean; errors: string[] } {
    if (value == null) {
      return {
        isValid: true,
        errors: EMPTY_ARRAY,
      };
    }

    if (!isValidPhoneNumber(value)) {
      return { isValid: false, errors: [t`Phone number is invalid`] };
    }

    return validateByConstraints(fieldVersion.fieldConstraints, value);
  }

  /**
   * Clear the provided phone value if it is invalid. Used to enforce validation on edit and paste
   * in Tables Alpha.
   *
   * See: https://regrello.slack.com/archives/C05NGEBLE1E/p1759863355149869
   */
  // (clewis): Define as an arrow function to avoid 'this' issues when passing to cellEditorParams.
  private clearValueIfInvalid = (value: VALUE): VALUE => {
    if (value == null) {
      return null;
    }

    const trimmedValue = value.trim();
    if (trimmedValue === EMPTY_STRING) {
      return trimmedValue;
    }

    if (!isValidPhoneNumber(trimmedValue)) {
      return this.getEmptyValueForFrontend();
    }

    return value;
  };
}
